import type { MenuLink } from "core-fe-lib/components-shadcn/types/menu-link";
import type { AppContext } from "core-fe-lib/components-shadcn/shell/types";
import { Role } from "core-fe-lib/openapi/core/models/Role";
import { useLmsCapabilitiesStore } from "@/modules/lms/stores/lms-capabilities";

// Two spaces (ADR 023): the learner space is shown to every logged-in user; the
// editor space ("Course Studio") only appears for authors — admins or holders
// of an LMS editor grant. canAuthor is a server flag, so we load it lazily and
// read it reactively; admins also pass synchronously via hasPrivilege so their
// studio nav shows before the flag resolves.
export function getLmsLinks(ctx: AppContext): MenuLink[] {
  const t = ctx.t;
  const caps = useLmsCapabilitiesStore();
  // Side-effect is idempotent (single fetch, deduped); it lets the studio
  // section appear for non-admin editors once the flag resolves.
  void caps.ensureLoaded();

  const links: MenuLink[] = [
    {
      title: t("lms.title"),
      caption: t("lms.caption"),
      icon: "school",
      hasExpansion: true,
      items: [
        { title: t("lms.catalog.title"), icon: "menu_book", link: "/lms" },
        {
          title: t("lms.myLearning.menuTitle"),
          icon: "person",
          link: "/lms/my-learning",
        },
        {
          title: t("lms.review.title"),
          icon: "history",
          link: "/lms/review",
        },
      ],
    },
  ];

  if (caps.canAuthor || ctx.userStore.hasPrivilege(Role.ADMIN)) {
    links.push({
      title: t("lms.studio.title"),
      caption: t("lms.studio.caption"),
      icon: "edit",
      hasExpansion: true,
      items: [
        {
          title: t("lms.admin.menuTitle"),
          icon: "menu_book",
          link: "/lms/admin/courses",
        },
        {
          title: t("lms.space.menuTitle"),
          icon: "storefront",
          link: "/lms/admin/space",
        },
        {
          title: t("lms.admin.runs"),
          icon: "history",
          link: "/lms/admin/runs",
          requiredPrivilege: Role.ADMIN,
          linkType: "ADMIN",
        },
        {
          title: t("lms.admin.assistantSettings"),
          icon: "smart_toy",
          link: "/lms/admin/employees",
          requiredPrivilege: Role.ADMIN,
          linkType: "ADMIN",
        },
        {
          title: t("lms.editors.menuTitle"),
          icon: "group",
          link: "/lms/admin/editors",
          requiredPrivilege: Role.CUSTOMER_ADMIN,
          linkType: "ADMIN",
        },
        {
          title: t("lms.admin.media"),
          icon: "image",
          link: "/lms/admin/assets",
          requiredPrivilege: Role.SUPER_ADMIN,
          linkType: "ADMIN",
        },
      ],
    });
  }

  return links;
}
